import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";

export default function CookiePage() {
  useEffect(() => { document.title = "Tavoli da Gioco — Cookie Policy"; }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] dark:bg-[#181818]">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-[#2B593F] dark:text-[#8FC79F] hover:underline mb-6"
        >
          <ArrowLeft size={14} /> Torna al dashboard
        </Link>

        <h1 className="font-display text-3xl font-bold text-[#2D2A26] dark:text-[#F5F1EA] tracking-tight mb-2">Cookie Policy</h1>
        <p className="text-sm text-[#757169] dark:text-[#B8B2AA] dark:text-[#B8B2AA] mb-8">Ultimo aggiornamento: maggio 2025</p>

        <div className="bg-white dark:bg-[#111111] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-6 sm:p-8 space-y-6 text-sm text-[#4A4742] dark:text-[#C8C2BA] leading-relaxed">

          <section>
            <h2 className="font-semibold text-[#2D2A26] dark:text-[#F5F1EA] text-base mb-2">Cosa sono i cookie</h2>
            <p>
              I cookie sono piccoli file di testo che i siti web salvano nel browser dell'utente durante
              la navigazione. Servono a far funzionare correttamente il sito, ricordare le preferenze
              dell'utente e — in certi casi — raccogliere informazioni statistiche sull'utilizzo.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-[#2D2A26] dark:text-[#F5F1EA] text-base mb-2">Cookie tecnici (necessari)</h2>
            <p>
              Questo sito utilizza esclusivamente cookie tecnici strettamente necessari al funzionamento
              del servizio. Non è richiesto il consenso per questi cookie ai sensi dell'art. 122
              del Codice Privacy e delle Linee Guida del Garante.
            </p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[#F5F1EA]">
                    <th className="text-left px-3 py-2 border border-[#EAE3D9] font-semibold text-[#2D2A26]">Nome</th>
                    <th className="text-left px-3 py-2 border border-[#EAE3D9] font-semibold text-[#2D2A26]">Finalità</th>
                    <th className="text-left px-3 py-2 border border-[#EAE3D9] font-semibold text-[#2D2A26]">Durata</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="px-3 py-2 border border-[#EAE3D9] font-mono">auth_token</td>
                    <td className="px-3 py-2 border border-[#EAE3D9]">Mantiene la sessione di accesso autenticata</td>
                    <td className="px-3 py-2 border border-[#EAE3D9]">Sessione / 7 giorni</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="font-semibold text-[#2D2A26] dark:text-[#F5F1EA] text-base mb-2">Cookie analitici</h2>
            <p>
              Il sito utilizza <strong>PostHog</strong> per raccogliere dati anonimi sull'utilizzo
              delle funzionalità (es. quali pagine vengono visitate, errori riscontrati).
              I dati sono aggregati e non consentono l'identificazione del singolo utente.
              PostHog è configurato in modalità <em>identified_only</em>: profili personali vengono
              creati solo dopo il login esplicito.
            </p>
            <p className="mt-2">
              Per maggiori informazioni: <a href="https://posthog.com/privacy" target="_blank" rel="noreferrer" className="text-[#2B593F] hover:underline">posthog.com/privacy</a>.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-[#2D2A26] dark:text-[#F5F1EA] text-base mb-2">Cookie di terze parti</h2>
            <p>
              Non sono presenti cookie pubblicitari, di profilazione o di tracciamento di terze parti.
              I font Google (Inter, Fraunces) vengono caricati da Google Fonts; Google potrebbe
              impostare cookie tecnici propri — consulta la{" "}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-[#2B593F] hover:underline">
                Privacy Policy di Google
              </a>.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-[#2D2A26] dark:text-[#F5F1EA] text-base mb-2">Come gestire i cookie</h2>
            <p>
              Puoi eliminare o bloccare i cookie in qualsiasi momento dalle impostazioni del tuo browser:
            </p>
            <ul className="list-disc list-inside mt-2 space-y-1 text-[#757169] dark:text-[#B8B2AA]">
              <li><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noreferrer" className="text-[#2B593F] hover:underline">Google Chrome</a></li>
              <li><a href="https://support.mozilla.org/it/kb/protezione-antitracciamento-avanzata-firefox" target="_blank" rel="noreferrer" className="text-[#2B593F] hover:underline">Mozilla Firefox</a></li>
              <li><a href="https://support.apple.com/it-it/guide/safari/sfri11471/mac" target="_blank" rel="noreferrer" className="text-[#2B593F] hover:underline">Apple Safari</a></li>
            </ul>
            <p className="mt-2">
              Attenzione: disabilitare i cookie tecnici potrebbe compromettere il funzionamento del login.
            </p>
          </section>

          <section>
            <h2 className="font-semibold text-[#2D2A26] dark:text-[#F5F1EA] text-base mb-2">Contatti</h2>
            <p>
              Per qualsiasi domanda sui cookie scrivi a{" "}
              <a href="mailto:info@tavolidagioco.eu" className="text-[#2B593F] hover:underline">info@tavolidagioco.eu</a>.
            </p>
          </section>

        </div>
      </main>
    </div>
  );
}
